import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Table, Tag, Dropdown, Modal, Select, Radio, Input, message, Space, Button, Switch } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  listEntitlements, listCatalog, grantEntitlement, transitionEntitlement, entitlementHistory,
  type Entitlement, type CatalogProduct, type EntitlementHistoryRow,
} from '../../services/tenantService';

// Features & Products control plane (E2) — the ONE tenant feature surface. Renders the
// FULL active catalogue (not just granted rows) so platform staff see every sellable
// product with an on/off switch: ON = grant ACTIVE (dependency-aware), OFF = disable
// (reason required, audited). Lifecycle detail (trial/suspend/history) stays in the
// row actions. Replaces the old hardcoded "Features" card, whose legacy codes
// (payroll_module, training_module, …) never matched the catalogue.
// See docs/tenant-product-entitlements-architecture.md.

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'green', TRIAL: 'blue', SUSPENDED: 'orange',
  PENDING: 'default', DISABLED: 'default', EXPIRED: 'red', TERMINATED: 'red',
};

// Mirror of the backend TRANSITIONS state machine (which `from` states allow each action).
function actionsFor(status: string): string[] {
  const a: string[] = [];
  if (['PENDING', 'TRIAL', 'SUSPENDED'].includes(status)) a.push('activate');
  if (['PENDING', 'DISABLED'].includes(status)) a.push('trial');
  if (['TRIAL', 'ACTIVE'].includes(status)) a.push('suspend');
  if (status === 'SUSPENDED') a.push('reactivate');
  if (['PENDING', 'TRIAL', 'ACTIVE', 'SUSPENDED', 'EXPIRED'].includes(status)) a.push('disable');
  return a;
}
const ACTION_LABEL: Record<string, string> = {
  activate: 'Activate', trial: 'Start trial', suspend: 'Suspend',
  reactivate: 'Reactivate', disable: 'Disable',
};
const NEEDS_REASON = new Set(['suspend', 'disable']);

// Products with a structured per-grant configuration form (entitlement !=
// configuration: the switch says a tenant MAY use it; this says HOW).
const CONFIGURABLE = new Set(['dana_localization']);

const PERSONA_PACKS = [
  { value: '', label: 'Auto (from country/region)' },
  { value: 'tz-dar', label: 'Tanzania — Dar es Salaam' },
  { value: 'tz-standard', label: 'Tanzania — Standard' },
  { value: 'ke-standard', label: 'Kenya — Standard' },
  { value: 'us-standard', label: 'United States — Standard' },
];
const LOC_COUNTRIES = ['TZ', 'KE', 'RW', 'UG', 'ZA', 'NG', 'GH', 'US'];
const REGIONS: Record<string, { value: string; label: string }[]> = {
  TZ: [{ value: '', label: 'National' }, { value: 'dar_es_salaam', label: 'Dar es Salaam' }],
  KE: [{ value: '', label: 'National' }, { value: 'nairobi', label: 'Nairobi' }],
};

export const TenantEntitlements: React.FC<{ tenantId: string; canManage: boolean }> = ({ tenantId, canManage }) => {
  const [rows, setRows] = useState<Entitlement[]>([]);
  const [catalog, setCatalog] = useState<CatalogProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantCode, setGrantCode] = useState<string>();
  const [grantStatus, setGrantStatus] = useState<'ACTIVE' | 'TRIAL'>('TRIAL');
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState<EntitlementHistoryRow[] | null>(null);
  const [configFor, setConfigFor] = useState<Entitlement | null>(null);
  const [cfg, setCfg] = useState<Record<string, any>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([listEntitlements(tenantId), listCatalog().catch(() => [])]);
      setRows(e);
      setCatalog(c);
    } catch {
      message.error('Could not load entitlements.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { void load(); }, [load]);

  const grant = useCallback(
    async (productCode: string, status: 'ACTIVE' | 'TRIAL', alsoDeps = false) => {
      setBusy(true);
      try {
        await grantEntitlement(tenantId, { productCode, status });
        message.success('Product granted.');
        setGrantOpen(false);
        setGrantCode(undefined);
        await load();
      } catch (e: any) {
        const data = e?.response?.data;
        if (data?.code === 'dependencies_unmet' && !alsoDeps && Array.isArray(data.missing)) {
          Modal.confirm({
            title: 'Missing dependencies',
            content: `${data.message} Enable them too?`,
            okText: 'Enable all',
            onOk: async () => {
              for (const m of data.missing) await grantEntitlement(tenantId, { productCode: m.code, status: 'ACTIVE' });
              await grant(productCode, status, true);
            },
          });
        } else {
          message.error(data?.message || 'Could not grant product.');
        }
      } finally {
        setBusy(false);
      }
    },
    [tenantId, load],
  );

  const runAction = useCallback(
    (row: Entitlement, action: string) => {
      const go = async (reason?: string) => {
        try {
          await transitionEntitlement(tenantId, row.id, action, reason ? { reason } : undefined);
          message.success(`${ACTION_LABEL[action] ?? action} done.`);
          await load();
        } catch (e: any) {
          message.error(e?.response?.data?.message || `Could not ${action}.`);
        }
      };
      if (NEEDS_REASON.has(action)) {
        let reason = '';
        Modal.confirm({
          title: `${ACTION_LABEL[action]} ${row.productName}`,
          content: (
            <div>
              <p className="text-sm text-muted-foreground mb-2">
                This affects the tenant's access to {row.productName}. Provide a reason (recorded in the audit log).
              </p>
              <Input.TextArea rows={2} onChange={(ev) => (reason = ev.target.value)} placeholder="Reason" />
            </div>
          ),
          okText: ACTION_LABEL[action],
          okButtonProps: { danger: action === 'disable' },
          onOk: () => go(reason.trim() || undefined),
        });
      } else {
        void go();
      }
    },
    [tenantId, load],
  );

  const openConfig = useCallback((row: Entitlement) => {
    const c = (row.configuration as Record<string, any>) ?? {};
    setCfg({
      country: c.country ?? 'TZ',
      region: c.region ?? '',
      primary_language: c.primary_language ?? 'sw',
      secondary_languages: c.secondary_languages ?? ['en'],
      code_switching: c.code_switching ?? true,
      regional_lingo: c.regional_lingo ?? true,
      lingo_strength: c.lingo_strength ?? 'moderate',
      formality: c.formality ?? 'professional_friendly',
      persona_pack: c.persona_pack ?? '',
      persona_version: c.persona_version ?? 'v1',
    });
    setConfigFor(row);
  }, []);

  const saveConfig = useCallback(async () => {
    if (!configFor) return;
    setBusy(true);
    try {
      const configuration: Record<string, any> = { ...cfg };
      if (!configuration.persona_pack) { delete configuration.persona_pack; delete configuration.persona_version; }
      if (!configuration.region) delete configuration.region;
      // Re-grant with the same status carries the new configuration (audited).
      const status = configFor.status === 'TRIAL' ? 'TRIAL' : 'ACTIVE';
      await grantEntitlement(tenantId, { productCode: configFor.productCode, status, configuration });
      message.success('Configuration saved.');
      setConfigFor(null);
      await load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save configuration.');
    } finally {
      setBusy(false);
    }
  }, [configFor, cfg, tenantId, load]);

  const showHistory = useCallback(
    async (row: Entitlement) => {
      setHistory([]);
      try {
        setHistory(await entitlementHistory(tenantId, row.id));
      } catch {
        message.error('Could not load history.');
        setHistory(null);
      }
    },
    [tenantId],
  );

  const entitledCodes = new Set(rows.map((r) => r.productCode));
  const grantable = catalog.filter((p) => p.status === 'active' && !entitledCodes.has(p.code));

  // ONE row per sellable product: the full active catalogue merged with the tenant's
  // entitlement (if any), plus any entitled rows not in the catalogue (partner grants).
  type FeatureRow = {
    key: string;
    productCode: string;
    productName: string;
    productType: string | null;
    ent: Entitlement | null;
  };
  const featureRows: FeatureRow[] = useMemo(() => {
    const entByCode = new Map(rows.map((r) => [r.productCode, r]));
    const out: FeatureRow[] = catalog
      .filter((p) => p.status === 'active')
      .map((p) => ({
        key: p.code, productCode: p.code, productName: p.name,
        productType: p.productType ?? null, ent: entByCode.get(p.code) ?? null,
      }));
    const catCodes = new Set(out.map((r) => r.productCode));
    for (const e of rows) {
      if (!catCodes.has(e.productCode)) {
        out.push({ key: e.productCode, productCode: e.productCode, productName: e.productName, productType: e.productType ?? null, ent: e });
      }
    }
    // Enabled first, then alphabetical — the tenant's live surface reads at a glance.
    return out.sort((a, b) =>
      Number(!!b.ent?.isEnabled) - Number(!!a.ent?.isEnabled) || a.productName.localeCompare(b.productName));
  }, [rows, catalog]);

  // The quick switch: ON = grant ACTIVE (dependency-aware, re-grant over any prior
  // row), OFF = the disable transition (reason modal via runAction).
  const toggleProduct = useCallback(
    (r: FeatureRow, on: boolean) => {
      if (on) void grant(r.productCode, 'ACTIVE');
      else if (r.ent) runAction(r.ent, 'disable');
    },
    [grant, runAction],
  );

  const columns = [
    {
      title: 'Product',
      key: 'product',
      render: (_: unknown, r: FeatureRow) => (
        <div>
          <div className="font-medium text-foreground">{r.productName}</div>
          <div className="text-xs text-muted-foreground" title={r.productCode}>
            {r.productCode.startsWith('partner:') ? 'partner product' : r.productCode}
            {r.productType ? ` · ${r.productType.replace(/_/g, ' ').toLowerCase()}` : ''}
          </div>
        </div>
      ),
    },
    {
      title: 'Status', key: 'status',
      render: (_: unknown, r: FeatureRow) =>
        r.ent
          ? <Tag color={STATUS_COLOR[r.ent.status] ?? 'default'}>{r.ent.status}</Tag>
          : <Tag>NOT GRANTED</Tag>,
    },
    {
      title: 'Source', key: 'source',
      render: (_: unknown, r: FeatureRow) =>
        <span className="text-xs text-muted-foreground">{r.ent ? r.ent.source.replace(/_/g, ' ').toLowerCase() : '—'}</span>,
    },
    {
      title: 'Enabled', key: 'enabled', align: 'center' as const,
      render: (_: unknown, r: FeatureRow) => (
        <Switch
          size="small"
          checked={!!r.ent?.isEnabled}
          disabled={!canManage || busy}
          onChange={(v) => toggleProduct(r, v)}
        />
      ),
    },
    {
      title: '', key: 'actions', align: 'right' as const,
      render: (_: unknown, r: FeatureRow) => {
        if (!r.ent) return null;
        const e = r.ent;
        const items = [
          ...(canManage && CONFIGURABLE.has(e.productCode) ? [{ key: 'configure', label: 'Configure…' }] : []),
          ...actionsFor(e.status).filter(() => canManage).map((a) => ({ key: a, label: ACTION_LABEL[a], danger: a === 'disable' })),
          { key: 'history', label: 'View history' },
        ];
        return (
          <Dropdown
            menu={{ items, onClick: ({ key }) => (key === 'history' ? void showHistory(e) : key === 'configure' ? openConfig(e) : runAction(e, key)) }}
            trigger={['click']}
          >
            <Button size="small">Actions ▾</Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Features &amp; Products</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            What this tenant can see and use. Switch a product on to grant it; off to disable (reason required).
          </p>
        </div>
        {canManage && grantable.length > 0 && (
          <Button type="primary" size="small" onClick={() => setGrantOpen(true)}>
            Grant as trial…
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Table
          rowKey="key"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={featureRows}
          pagination={false}
          locale={{ emptyText: 'No products in the catalogue yet.' }}
        />
      </CardContent>

      <Modal
        title="Grant a product"
        open={grantOpen}
        confirmLoading={busy}
        onOk={() => grantCode && grant(grantCode, grantStatus)}
        okButtonProps={{ disabled: !grantCode }}
        onCancel={() => setGrantOpen(false)}
        okText="Grant"
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <div className="text-sm font-medium mb-1">Product</div>
            <Select
              style={{ width: '100%' }}
              placeholder="Choose a product"
              value={grantCode}
              onChange={setGrantCode}
              options={grantable.map((p) => ({ value: p.code, label: `${p.name} (${p.productType.replace(/_/g, ' ').toLowerCase()})` }))}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div>
            <div className="text-sm font-medium mb-1">Access</div>
            <Radio.Group value={grantStatus} onChange={(e) => setGrantStatus(e.target.value)}>
              <Radio value="ACTIVE">Active</Radio>
              <Radio value="TRIAL">Trial</Radio>
            </Radio.Group>
          </div>
        </Space>
      </Modal>

      <Modal
        title="Regional voice & localization"
        open={configFor !== null}
        confirmLoading={busy}
        onOk={() => void saveConfig()}
        onCancel={() => setConfigFor(null)}
        okText="Save configuration"
      >
        <p className="text-xs text-muted-foreground mb-3">
          How Dana speaks for this tenant — country, language mix and regional persona.
          Persona changes presentation only; permissions, tools and confirmation rules are unaffected.
        </p>
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-sm font-medium mb-1">Country</div>
              <Select style={{ width: '100%' }} value={cfg.country}
                onChange={(v) => setCfg((c) => ({ ...c, country: v, region: '' }))}
                options={LOC_COUNTRIES.map((c) => ({ value: c, label: c }))} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Region</div>
              <Select style={{ width: '100%' }} value={cfg.region}
                onChange={(v) => setCfg((c) => ({ ...c, region: v }))}
                options={REGIONS[cfg.country] ?? [{ value: '', label: 'National' }]} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Primary language</div>
              <Select style={{ width: '100%' }} value={cfg.primary_language}
                onChange={(v) => setCfg((c) => ({ ...c, primary_language: v }))}
                options={[{ value: 'sw', label: 'Kiswahili' }, { value: 'en', label: 'English' }]} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Secondary languages</div>
              <Select mode="multiple" style={{ width: '100%' }} value={cfg.secondary_languages}
                onChange={(v) => setCfg((c) => ({ ...c, secondary_languages: v }))}
                options={[{ value: 'en', label: 'English' }, { value: 'sw', label: 'Kiswahili' }]} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Local flavor</div>
              <Select style={{ width: '100%' }} value={cfg.lingo_strength}
                onChange={(v) => setCfg((c) => ({ ...c, lingo_strength: v }))}
                options={[{ value: 'low', label: 'Low' }, { value: 'moderate', label: 'Moderate' }, { value: 'strong', label: 'Strong' }]} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Formality</div>
              <Select style={{ width: '100%' }} value={cfg.formality}
                onChange={(v) => setCfg((c) => ({ ...c, formality: v }))}
                options={[
                  { value: 'relaxed', label: 'Relaxed' },
                  { value: 'professional_friendly', label: 'Professional, friendly' },
                  { value: 'professional', label: 'Professional' },
                  { value: 'formal', label: 'Formal' },
                ]} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Persona</div>
              <Select style={{ width: '100%' }} value={cfg.persona_pack}
                onChange={(v) => setCfg((c) => ({ ...c, persona_pack: v }))}
                options={PERSONA_PACKS} />
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Persona version</div>
              <Select style={{ width: '100%' }} value={cfg.persona_version}
                disabled={!cfg.persona_pack}
                onChange={(v) => setCfg((c) => ({ ...c, persona_version: v }))}
                options={[{ value: 'v1', label: 'v1' }]} />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className="text-sm">Natural code switching (mirror the user's mix)</span>
            <Switch size="small" checked={!!cfg.code_switching}
              onChange={(v) => setCfg((c) => ({ ...c, code_switching: v }))} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">Regional conversational style</span>
            <Switch size="small" checked={!!cfg.regional_lingo}
              onChange={(v) => setCfg((c) => ({ ...c, regional_lingo: v }))} />
          </div>
        </Space>
      </Modal>

      <Modal title="Entitlement history" open={history !== null} footer={null} onCancel={() => setHistory(null)}>
        {history === null ? null : history.length === 0 ? (
          <p className="text-sm text-muted-foreground">No history yet.</p>
        ) : (
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.id} className="border-b border-border pb-2">
                <div className="text-sm font-medium">
                  {h.action.replace('entitlement.', '')}
                  {h.newState?.status ? ` → ${h.newState.status}` : ''}
                </div>
                <div className="text-xs text-muted-foreground">
                  {h.timestamp ? fmtDateTime(h.timestamp) : ''}
                  {h.metadata?.reason ? ` · ${h.metadata.reason}` : ''}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default TenantEntitlements;
